import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CaretRight,
  CaretDown,
  EnvelopeSimple,
  FileDoc,
  FolderPlus,
  Image as ImageIcon,
  PencilSimple,
  Printer,
  Trash,
  UploadSimple,
} from '@phosphor-icons/react';
import { API, BACKEND_ORIGIN } from '../config';

function normalizeMobilePhotos(raw) {
  let list = [];
  if (Array.isArray(raw)) list = raw;
  else if (raw == null) list = [];
  else if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      list = Array.isArray(p) ? p : [];
    } catch {
      list = [];
    }
  }
  return list.filter((p) => p && typeof p === 'object' && typeof p.url === 'string' && p.url.length > 0);
}

function photoCategoryLabel(category, t) {
  if (category === 'before') return t('filesPhotoBefore');
  if (category === 'during') return t('filesPhotoDuring');
  if (category === 'after') return t('filesPhotoAfter');
  if (category === 'glasbok') return t('filesPhotoGlasbok');
  return t('filesPhotoGeneric');
}

export function fileKind(name, mime) {
  const lower = (name || '').toLowerCase();
  const m = (mime || '').toLowerCase();
  if (lower.endsWith('.pdf') || m.includes('pdf')) return 'pdf';
  if (/\.(png|jpe?g|webp|gif|bmp|heic)$/i.test(lower) || m.startsWith('image/')) return 'image';
  if (lower.endsWith('.msg')) return 'msg';
  if (/\.(doc|docx)$/i.test(lower) || m.includes('word')) return 'doc';
  if (/\.(xls|xlsx|csv)$/i.test(lower) || m.includes('sheet') || m.includes('excel')) return 'sheet';
  return 'generic';
}

/** Windows-style red “PDF” document icon */
export function IconPdf({ className = '', size = 20 }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path d="M4 3.5C4 2.67 4.67 2 5.5 2h9.2L20 7.3V20.5c0 .83-.67 1.5-1.5 1.5h-13A1.5 1.5 0 014 20.5v-17z" fill="#fff" stroke="#c4cdd6" strokeWidth="1" />
      <path d="M14 2v5.5h5" fill="#eef2f6" stroke="#c4cdd6" strokeWidth="1" strokeLinejoin="round" />
      <rect x="4" y="7" width="16" height="6" rx="0.5" fill="#d92d20" />
      <text x="7.2" y="11.6" fill="#fff" fontSize="5.2" fontWeight="700" fontFamily="system-ui, Segoe UI, sans-serif">
        PDF
      </text>
      <rect x="6" y="15" width="12" height="1.2" rx="0.3" fill="#e2e8f0" />
      <rect x="6" y="17.2" width="9" height="1.2" rx="0.3" fill="#e2e8f0" />
    </svg>
  );
}

/** Manila-style closed folder (unique gradient ids per instance) */
export function IconFolderClosed({ className = '', size = 20, uid = 'f' }) {
  const gt = `ofp-tab-${uid}`;
  const gb = `ofp-body-${uid}`;
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        <linearGradient id={gt} x1="3" y1="4" x2="14" y2="11" gradientUnits="userSpaceOnUse">
          <stop stopColor="#fde68a" />
          <stop offset="1" stopColor="#f59e0b" />
        </linearGradient>
        <linearGradient id={gb} x1="3" y1="12" x2="21" y2="22" gradientUnits="userSpaceOnUse">
          <stop stopColor="#fcd34d" />
          <stop offset="1" stopColor="#d97706" />
        </linearGradient>
      </defs>
      <path d="M3 9.5V19a2 2 0 002 2h14a2 2 0 002-2v-9.5H3z" fill={`url(#${gb})`} stroke="#b45309" strokeWidth="0.75" />
      <path d="M3 9.5V8a2 2 0 012-2h5.5l2 3.5H21v0H3z" fill={`url(#${gt})`} stroke="#b45309" strokeWidth="0.75" strokeLinejoin="round" />
    </svg>
  );
}

export function IconImageFile({ className = '', size = 20 }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="4" y="3" width="16" height="18" rx="1.5" fill="#fff" stroke="#94a3b8" strokeWidth="1" />
      <rect x="6.5" y="6" width="11" height="7" rx="0.5" fill="#e0f2fe" stroke="#38bdf8" strokeWidth="0.5" />
      <circle cx="9" cy="8.5" r="1" fill="#0ea5e9" />
      <path d="M7 14l2.5-2 2 2 3-3 3 3v3.5H7V14z" fill="#7dd3fc" opacity="0.9" />
    </svg>
  );
}

export function IconMailFile({ className = '', size = 20 }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="4" y="5" width="16" height="14" rx="1.5" fill="#fff" stroke="#94a3b8" strokeWidth="1" />
      <path d="M4 8l8 5 8-5" stroke="#64748b" strokeWidth="1" fill="none" />
      <rect x="7" y="14" width="10" height="1.2" rx="0.3" fill="#cbd5e1" />
    </svg>
  );
}

export function TreeFileGlyph({ kind, size = 20 }) {
  switch (kind) {
    case 'pdf':
      return <IconPdf size={size} className="shrink-0" />;
    case 'image':
      return <IconImageFile size={size} className="shrink-0" />;
    case 'msg':
      return <IconMailFile size={size} className="shrink-0" />;
    case 'doc':
      return <FileDoc size={size} weight="duotone" className="shrink-0 text-blue-600" />;
    case 'sheet':
      return <FileDoc size={size} weight="duotone" className="shrink-0 text-emerald-700" />;
    default:
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" className="shrink-0 text-slate-500" aria-hidden>
          <path
            fill="currentColor"
            d="M6 2h9l5 5v15a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2zm8 1.5V8h4.5L14 3.5z"
            opacity="0.9"
          />
        </svg>
      );
  }
}

/**
 * Dotted tree guides (Explorer-style).
 * lineage[i] === true → vertical continues through the bottom of this row (more nodes below at that level).
 */
/** Folder id + every ancestor id so the tree can expand only the path to an upload target. */
function folderAncestorIds(folderId, folders) {
  const byId = new Map((folders || []).map((f) => [String(f.id), f]));
  const ids = [];
  const seen = new Set();
  let cur = byId.get(String(folderId));
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    ids.push(cur.id);
    cur = cur.parent_id != null ? byId.get(String(cur.parent_id)) : null;
  }
  return ids;
}

export function TreeGuideGutter({ lineage }) {
  if (!lineage.length) return null;
  const last = lineage.length - 1;
  return (
    <div className="order-files-tree-guides flex h-full min-h-[30px] shrink-0 self-stretch" aria-hidden>
      {lineage.map((continues, col) => {
        const isLastCol = col === last;
        if (!isLastCol) {
          return (
            <div key={col} className="order-files-tree-guide-col relative w-[18px] min-h-[30px]">
              <div
                className={`order-files-tree-guide-v absolute left-[7px] top-0 w-0 ${
                  continues ? 'bottom-0' : 'h-1/2'
                }`}
              />
            </div>
          );
        }
        return (
          <div key={col} className="order-files-tree-guide-col relative w-[18px] min-h-[30px]">
            <div className="order-files-tree-guide-v absolute left-[7px] top-0 h-1/2 w-0" />
            {continues ? <div className="order-files-tree-guide-v absolute bottom-0 left-[7px] top-1/2 w-0" /> : null}
            <div className="order-files-tree-guide-h absolute left-[7px] right-0 top-1/2 h-0 -translate-y-px" />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Per-order file tree: default folders + uploads; mobile photos appear under "Fotos".
 */
export default function OrderFilesPanel({
  orderId,
  orderTitle,
  mobilePhotosJson,
  onRequestDeleteMobilePhoto,
  t,
  language,
}) {
  const [tree, setTree] = useState({ folders: [], files: [], mobile_photos: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const [selected, setSelected] = useState(null);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const fileInputRef = useRef(null);
  const uploadTargetFolderIdRef = useRef(null);
  const initialRootsExpandedRef = useRef(false);

  const mobileLocal = useMemo(() => normalizeMobilePhotos(mobilePhotosJson), [mobilePhotosJson]);

  const loadTree = useCallback(async (opts) => {
    const expandFolderId = opts?.expandFolderId;
    if (!orderId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/orders/${orderId}/files/tree`, { withCredentials: true });
      setTree(res.data);
      const folders = res.data.folders || [];
      const rootIds = folders.filter((f) => !f.parent_id).map((f) => f.id);
      setExpanded((prev) => {
        const next = new Set(prev);
        if (expandFolderId != null) {
          folderAncestorIds(expandFolderId, folders).forEach((id) => next.add(id));
          return next;
        }
        if (!initialRootsExpandedRef.current) {
          initialRootsExpandedRef.current = true;
          rootIds.forEach((id) => next.add(id));
        }
        return next;
      });
    } catch {
      toast.error(t('filesLoadFailed'));
      setTree({ folders: [], files: [], mobile_photos: [] });
    } finally {
      setLoading(false);
    }
  }, [orderId, t]);

  useEffect(() => {
    initialRootsExpandedRef.current = false;
  }, [orderId]);

  // Drop highlight can linger if a drag is cancelled outside a folder row.
  useEffect(() => {
    const clear = () => setDragOverFolderId(null);
    window.addEventListener('dragend', clear);
    window.addEventListener('drop', clear);
    return () => {
      window.removeEventListener('dragend', clear);
      window.removeEventListener('drop', clear);
    };
  }, []);

  useEffect(() => {
    void loadTree();
  }, [loadTree]);

  const foldersByParent = useMemo(() => {
    const m = new Map();
    for (const f of tree.folders || []) {
      const k = f.parent_id || '__root__';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(f);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => (a.sort_order - b.sort_order) || String(a.name).localeCompare(String(b.name), language === 'en' ? 'en' : 'nl'));
    }
    return m;
  }, [tree.folders, language]);

  const fotosFolderId = useMemo(() => {
    const f = (tree.folders || []).find((x) => x.slug === 'fotos');
    return f ? f.id : null;
  }, [tree.folders]);

  const rows = useMemo(() => {
    const out = [];
    const files = tree.files || [];

    function emitFolderContents(parentFolder, lineagePrefix) {
      const fid = parentFolder.id;
      const subfolders = foldersByParent.get(fid) || [];
      const web = files.filter((fi) => String(fi.folder_id) === String(fid));
      const mobiles =
        fotosFolderId && String(fid) === String(fotosFolderId) ? mobileLocal.map((m, idx) => ({ m, idx })) : [];

      const items = [
        ...subfolders.map((f) => ({ type: 'folder', folder: f })),
        ...web.map((f) => ({ type: 'webfile', file: f })),
        ...mobiles.map(({ m, idx }) => ({ type: 'mobile', mobile: m, idx })),
      ];

      items.forEach((item, idx) => {
        const hasMoreBelow = idx < items.length - 1;
        const lineage = [...lineagePrefix, hasMoreBelow];

        if (item.type === 'folder') {
          const folder = item.folder;
          const isOpen = expanded.has(folder.id);
          out.push({ kind: 'folder', folder, lineage, isOpen });
          if (isOpen) {
            emitFolderContents(folder, lineage);
          }
        } else if (item.type === 'webfile') {
          out.push({ kind: 'webfile', file: item.file, lineage });
        } else {
          out.push({ kind: 'mobile', mobile: item.mobile, idx: item.idx, lineage });
        }
      });
    }

    const roots = foldersByParent.get('__root__') || [];
    roots.forEach((folder, i) => {
      const hasNextRoot = i < roots.length - 1;
      out.push({ kind: 'folder', folder, lineage: [], isOpen: expanded.has(folder.id) });
      if (expanded.has(folder.id)) {
        emitFolderContents(folder, [hasNextRoot]);
      }
    });

    return out;
  }, [foldersByParent, tree.files, expanded, fotosFolderId, mobileLocal]);

  const toggleExpand = (folderId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const absUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${BACKEND_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
  };

  const parentIdForNewFolder = () => {
    if (selected?.kind !== 'folder') return null;
    const f = selected.folder;
    if (!f.parent_id) return Number(f.id);
    return Number(f.parent_id);
  };

  const addFolder = async () => {
    const parentId = parentIdForNewFolder();
    const name = window.prompt(t('filesFolderNamePrompt'), '');
    if (name == null) return;
    const trimmed = String(name).trim();
    if (!trimmed) {
      toast.error(t('filesFolderNameRequired'));
      return;
    }
    try {
      await axios.post(
        `${API}/orders/${orderId}/files/folders`,
        { name: trimmed, parent_id: parentId },
        { withCredentials: true }
      );
      toast.success(t('filesFolderCreated'));
      await loadTree();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('filesFolderCreateFailed'));
    }
  };

  const deleteFolder = async () => {
    if (selected?.kind !== 'folder') {
      toast.error(t('filesSelectFolderFirst'));
      return;
    }
    if (selected.folder.is_default) {
      toast.error(t('filesCannotDeleteDefault'));
      return;
    }
    if (!window.confirm(t('filesConfirmDeleteFolder'))) return;
    try {
      await axios.delete(`${API}/orders/${orderId}/files/folders/${selected.folder.id}`, { withCredentials: true });
      toast.success(t('filesFolderDeleted'));
      setSelected(null);
      await loadTree();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('filesFolderDeleteFailed'));
    }
  };

  /** Toolbar trash: deletes whatever is selected (file, mobile photo, or custom folder). */
  const deleteSelection = async () => {
    if (selected?.kind === 'webfile') {
      await deleteWebFile(selected.file.id);
      return;
    }
    if (selected?.kind === 'mobile') {
      onRequestDeleteMobilePhoto?.(selected.mobile.url);
      return;
    }
    await deleteFolder();
  };

  const renameFolder = async () => {
    if (selected?.kind !== 'folder') {
      toast.error(t('filesSelectFolderFirst'));
      return;
    }
    const name = window.prompt(t('filesRenameFolderPrompt'), selected.folder.name);
    if (name == null) return;
    const trimmed = String(name).trim();
    if (!trimmed) {
      toast.error(t('filesFolderNameRequired'));
      return;
    }
    try {
      await axios.patch(
        `${API}/orders/${orderId}/files/folders/${selected.folder.id}`,
        { name: trimmed },
        { withCredentials: true }
      );
      toast.success(t('filesFolderRenamed'));
      await loadTree();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('filesFolderRenameFailed'));
    }
  };

  const pickUploadFolder = () => {
    if (selected?.kind === 'folder') return selected.folder.id;
    if (selected?.kind === 'webfile') return selected.file.folder_id;
    if (selected?.kind === 'mobile' && fotosFolderId) return fotosFolderId;
    return null;
  };

  const openUpload = () => {
    const fid = pickUploadFolder();
    if (!fid) {
      toast.error(t('filesSelectFolderForUpload'));
      return;
    }
    uploadTargetFolderIdRef.current = fid;
    fileInputRef.current?.click();
  };

  const onFileChange = async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    input.value = '';
    const fid = uploadTargetFolderIdRef.current;
    if (!files.length || !fid) return;
    try {
      for (const file of files) {
        const form = new FormData();
        form.append('upload', file);
        await axios.post(`${API}/orders/${orderId}/files/folders/${fid}/upload`, form, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success(
        files.length === 1
          ? t('filesUploadSuccess')
          : t('filesUploadMultipleSuccess').replace('{count}', String(files.length))
      );
      await loadTree({ expandFolderId: fid });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('filesUploadFailed'));
    }
  };

  const emailSelection = () => {
    let url = '';
    let label = '';
    if (selected?.kind === 'webfile') {
      url = absUrl(selected.file.url);
      label = selected.file.original_filename || '';
    } else if (selected?.kind === 'mobile') {
      url = absUrl(selected.mobile.url);
      label = photoCategoryLabel(selected.mobile.category, t);
    } else {
      toast.error(t('filesSelectFileForAction'));
      return;
    }
    const subject = encodeURIComponent(`${t('filesLabel')} — ${orderTitle || orderId}`);
    const body = encodeURIComponent(`${label}\n${url}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const printSelection = () => {
    let url = '';
    if (selected?.kind === 'webfile') {
      url = absUrl(selected.file.url);
    } else if (selected?.kind === 'mobile') {
      url = absUrl(selected.mobile.url);
    } else {
      toast.error(t('filesSelectFileForAction'));
      return;
    }
    const w = window.open(url, '_blank', 'noopener,noreferrer');
    if (w) {
      w.addEventListener(
        'load',
        () => {
          try {
            w.print();
          } catch {
            /* ignore */
          }
        },
        { once: true }
      );
    }
  };

  const viewSelection = () => {
    if (selected?.kind === 'webfile') {
      window.open(absUrl(selected.file.url), '_blank', 'noopener,noreferrer');
    } else if (selected?.kind === 'mobile') {
      window.open(absUrl(selected.mobile.url), '_blank', 'noopener,noreferrer');
    } else {
      toast.error(t('filesSelectFileForAction'));
    }
  };

  const NETWORK_DRAG_TYPE = 'application/x-vmk-network-file';

  const isNetworkDrag = (e) => Array.from(e.dataTransfer?.types || []).includes(NETWORK_DRAG_TYPE);

  const importNetworkFile = async (folderId, networkPath) => {
    try {
      await axios.post(
        `${API}/orders/${orderId}/files/folders/${folderId}/import-network`,
        { path: networkPath },
        { withCredentials: true }
      );
      toast.success(t('filesNetworkImported'));
      await loadTree({ expandFolderId: folderId });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('filesNetworkImportFailed'));
    }
  };

  const folderDropHandlers = (folderId) => ({
    onDragOver: (e) => {
      if (!isNetworkDrag(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setDragOverFolderId(folderId);
    },
    onDragLeave: () => {
      setDragOverFolderId((cur) => (cur === folderId ? null : cur));
    },
    onDrop: (e) => {
      if (!isNetworkDrag(e)) return;
      e.preventDefault();
      setDragOverFolderId(null);
      const networkPath = e.dataTransfer.getData(NETWORK_DRAG_TYPE);
      if (networkPath) void importNetworkFile(folderId, networkPath);
    },
  });

  const deleteWebFile = async (fileId) => {
    if (!window.confirm(t('filesConfirmDeleteFile'))) return;
    try {
      await axios.delete(`${API}/orders/${orderId}/files/${fileId}`, { withCredentials: true });
      toast.success(t('filesFileDeleted'));
      setSelected(null);
      await loadTree();
    } catch {
      toast.error(t('filesFileDeleteFailed'));
    }
  };

  const isRowSelected = (row) => {
    if (!selected) return false;
    if (row.kind === 'folder' && selected.kind === 'folder') return String(selected.folder.id) === String(row.folder.id);
    if (row.kind === 'webfile' && selected.kind === 'webfile') return String(selected.file.id) === String(row.file.id);
    if (row.kind === 'mobile' && selected.kind === 'mobile') return selected.idx === row.idx;
    return false;
  };

  const tbIcon =
    'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#e2e8f0] bg-white text-[#334155] shadow-sm transition hover:border-[#cbd5e1] hover:bg-[#f8fafc] disabled:pointer-events-none disabled:opacity-35 dark:border-[#334155] dark:bg-[#1e293b] dark:text-[#e2e8f0] dark:hover:bg-[#334155]';
  const tbText =
    'inline-flex h-9 min-w-[4.75rem] shrink-0 items-center justify-center rounded-md border border-[#e2e8f0] bg-white px-2 text-xs font-semibold text-[#334155] shadow-sm transition hover:border-[#cbd5e1] hover:bg-[#f8fafc] disabled:pointer-events-none disabled:opacity-35 dark:border-[#334155] dark:bg-[#1e293b] dark:text-[#e2e8f0] dark:hover:bg-[#334155]';

  return (
    <section className="vmk-card-flat p-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-[#94a3b8]">{t('filesLabel')}</h4>
        {orderTitle ? <p className="text-sm text-[#64748b] truncate max-w-xl">{orderTitle}</p> : null}
      </div>

      <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onFileChange} />

      <div className="order-files-toolbar mb-3 flex flex-wrap items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-2 py-1.5 dark:border-[#334155] dark:bg-[#0f172a]">
        <button type="button" className={tbIcon} title={t('filesAddFolder')} onClick={addFolder}>
          <FolderPlus size={20} weight="duotone" className="text-emerald-600" />
        </button>
        <button
          type="button"
          className={tbIcon}
          title={selected?.kind === 'folder' ? t('filesDeleteFolder') : t('filesDeleteFile')}
          onClick={deleteSelection}
          disabled={!selected || (selected.kind === 'folder' && !!selected.folder?.is_default)}
        >
          <Trash size={20} weight="duotone" className="text-red-600" />
        </button>
        <button type="button" className={tbIcon} title={t('filesUpload')} onClick={openUpload}>
          <UploadSimple size={20} weight="duotone" className="text-emerald-600" />
        </button>
        <span className="mx-0.5 h-6 w-px shrink-0 bg-[#e2e8f0] dark:bg-[#334155]" aria-hidden />
        <button type="button" className={tbIcon} title={t('filesEmail')} onClick={emailSelection} disabled={!selected || selected.kind === 'folder'}>
          <EnvelopeSimple size={20} weight="duotone" />
        </button>
        <button type="button" className={tbIcon} title={t('filesPrint')} onClick={printSelection} disabled={!selected || selected.kind === 'folder'}>
          <Printer size={20} weight="duotone" />
        </button>
        <button type="button" className={tbIcon} title={t('filesRenameFolder')} onClick={renameFolder} disabled={selected?.kind !== 'folder'}>
          <PencilSimple size={20} weight="duotone" />
        </button>
        <button
          type="button"
          className={`${tbText} ml-auto`}
          title={t('filesOpen')}
          onClick={viewSelection}
          disabled={!selected || selected.kind === 'folder'}
        >
          {t('filesOpen')}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12 text-sm text-[#64748b]">{t('filesLoading')}</div>
      ) : (
        <div
          className="order-files-tree-panel max-h-[calc(100vh-330px)] min-h-[200px] overflow-y-auto rounded-lg border border-[#cfd8e3] bg-[#fafbfc] font-sans text-[13px] leading-snug text-[#1e293b] shadow-inner select-none dark:border-[#334155] dark:bg-[#0f172a] dark:text-[#e2e8f0]"
          role="tree"
        >
          {rows.length === 0 ? (
            <p className="p-4 text-[#64748b]">{t('filesEmptyTree')}</p>
          ) : (
            <ul className="order-files-tree-list py-0.5">
              {rows.map((row) => {
                const sel = isRowSelected(row);
                const rowCls = `order-files-tree-row group flex min-h-[30px] items-stretch pr-2 transition-colors ${
                  sel ? 'order-files-tree-row-selected bg-[#dbeafe] dark:bg-blue-950/50' : 'hover:bg-white/90 dark:hover:bg-slate-800/50'
                }`;

                if (row.kind === 'folder') {
                  const { folder, isOpen, lineage } = row;
                  const isDropTarget = dragOverFolderId === folder.id;
                  return (
                    <li
                      key={`f-${folder.id}`}
                      className={`${rowCls} ${
                        isDropTarget ? 'bg-emerald-50 outline outline-2 -outline-offset-2 outline-emerald-400 dark:bg-emerald-950/40' : ''
                      }`}
                      {...folderDropHandlers(folder.id)}
                    >
                      <TreeGuideGutter lineage={lineage} />
                      <div className="flex min-w-0 flex-1 items-center gap-0.5 py-0.5">
                        <button
                          type="button"
                          className="flex h-8 w-7 shrink-0 items-center justify-center rounded text-[#475569] hover:bg-black/[0.04] dark:hover:bg-white/10"
                          aria-expanded={isOpen}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(folder.id);
                          }}
                        >
                          {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                        </button>
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 items-center gap-2 rounded py-0.5 text-left"
                          onClick={() => setSelected({ kind: 'folder', folder })}
                        >
                          <IconFolderClosed uid={String(folder.id)} size={20} className="shrink-0 drop-shadow-sm" />
                          <span className="truncate font-medium text-[#0f172a] dark:text-[#f1f5f9]">{folder.name}</span>
                        </button>
                      </div>
                    </li>
                  );
                }

                if (row.kind === 'webfile') {
                  const { file, lineage } = row;
                  const kind = fileKind(file.original_filename, file.mime_type);
                  return (
                    <li key={`w-${file.id}`} className={rowCls}>
                      <TreeGuideGutter lineage={lineage} />
                      <div className="flex min-w-0 flex-1 items-center gap-2 py-0.5 pl-1">
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 items-center gap-2 rounded py-0.5 text-left"
                          onClick={() => setSelected({ kind: 'webfile', file })}
                          onDoubleClick={() => window.open(absUrl(file.url), '_blank', 'noopener,noreferrer')}
                        >
                          <TreeFileGlyph kind={kind} size={20} />
                          <span className="truncate text-[#334155] dark:text-[#cbd5e1]">{file.original_filename}</span>
                        </button>
                        <button
                          type="button"
                          className="shrink-0 rounded p-1.5 text-[#94a3b8] opacity-0 transition hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-950/40"
                          title={t('filesDeleteFile')}
                          onClick={(e) => {
                            e.stopPropagation();
                            void deleteWebFile(file.id);
                          }}
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </li>
                  );
                }

                const { mobile, idx, lineage } = row;
                return (
                  <li key={`m-${mobile.url}-${idx}`} className={rowCls}>
                    <TreeGuideGutter lineage={lineage} />
                    <div className="flex min-w-0 flex-1 items-center gap-2 py-0.5 pl-1">
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-center gap-2 rounded py-0.5 text-left"
                        onClick={() => setSelected({ kind: 'mobile', mobile, idx })}
                        onDoubleClick={() => window.open(absUrl(mobile.url), '_blank', 'noopener,noreferrer')}
                      >
                        <ImageIcon size={20} weight="duotone" className="shrink-0 text-[#00CBA9]" />
                        <span className="truncate text-[#334155] dark:text-[#cbd5e1]">
                          {photoCategoryLabel(mobile.category, t)} — {mobile.url.split('/').pop() || t('filesPhotoGeneric')}
                        </span>
                      </button>
                      <button
                        type="button"
                        className="shrink-0 rounded p-1.5 text-[#94a3b8] opacity-0 transition hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-950/40"
                        title={t('photoRemoveTitle')}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRequestDeleteMobilePhoto?.(mobile.url);
                        }}
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
